import axios from 'axios';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import Vue from 'vue';

export const ApiService = {
  // Stores the interceptor position so that it can be later ejected when needed
  _interceptor: null,

  init() {
    ApiService.mountInterceptor();
    ApiService.setHeader();
    AuthService.refreshTokenRoutine();
    AuthService.listenStorageChanges();
  },

  /**
   * The Authorization header for a request axios doesn't make (PDF.js, fetch): the session token goes in
   * a header, never in a URL, where it would end up in logs, history and Referer headers.
   */
  authorizationHeader() {
    const token = StorageService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  setHeader() {
    if (StorageService.getToken()) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${StorageService.getToken()}`;
    }
    axios.defaults.headers.common['Accept'] = 'application/json';
  },

  removeHeader() {
    axios.defaults.headers.common = {};
  },

  get(resource, params) {
    return axios.get(resource, params);
  },

  post(resource, data, params) {
    return axios.post(resource, data, params);
  },

  put(resource, data, params) {
    return axios.put(resource, data, params);
  },

  patch(resource, data, params) {
    return axios.patch(resource, data, params);
  },

  delete(resource, data, params) {
    return axios.delete(resource, data, params);
  },

  mountInterceptor() {
    this._interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (!error.response) throw error;

        let status = error.response.status;

        if (status === 401) {
          // If error comes from authenticate or logout request, throw the error
          let url = error.config && error.config.url ? error.config.url : '';
          if (url.endsWith('/auth') || url.includes('/auth?rememberMe')) throw error;

          // Logout the user
          const store = await import('../store/store');
          await store.default.dispatch('auth/logout');
          throw error;
        }

        throw error;
      },
    );
  },

  unmountInterceptor() {
    // Eject the interceptor
    axios.interceptors.response.eject(this._interceptor);
  },

  /**
   * Perform a custom Axios request.
   *
   * data is an object containing the following properties:
   *  - method
   *  - url
   *  - data ... request payload
   *  - auth (optional)
   *    - username
   *    - password
   **/
  customRequest(data) {
    return axios(data);
  },
};
